import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiJson, socket } from '../lib/api';
import logo from '../assets/chitconnect-logo-blue.png';

const toInt = (v) => {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

export default function DirectChat() {
  const { conversationId, userId } = useParams();

  const me = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const meId = toInt(me?.id);

  const [convId, setConvId] = useState(conversationId ? toInt(conversationId) : 0);
  const [otherUser, setOtherUser] = useState(userId ? { id: toInt(userId), name: '' } : null);
  const [dmState, setDmState] = useState({ other_last_read_message_id: 0, other_last_delivered_message_id: 0 });
  const [messages, setMessages] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const bottomRef = useRef(null);

  // Prevent spam: only POST delivered/read when the pointer advances (per conversation)
  const markRef = useRef({ convId: 0, delivered: 0, read: 0 });

  const markDeliveredRead = (cid, lastMessageId, opts = { delivered: true, read: true }) => {
    const conv = toInt(cid);
    const id = toInt(lastMessageId);
    if (!conv || !id) return;

    if (markRef.current.convId !== conv) {
      markRef.current = { convId: conv, delivered: 0, read: 0 };
    }

    if (opts?.delivered && id > toInt(markRef.current.delivered)) {
      markRef.current.delivered = id;
      apiJson(`/api/messages/dm/${conv}/delivered`, {
        method: 'POST',
        body: { last_delivered_message_id: id },
      }).catch(() => {});
    }

    if (opts?.read && id > toInt(markRef.current.read)) {
      markRef.current.read = id;
      apiJson(`/api/messages/dm/${conv}/read`, {
        method: 'POST',
        body: { last_read_message_id: id },
      }).catch(() => {});
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const dedupMerge = (prev, incoming, mode = 'append') => {
    const map = new Map(prev.map((m) => [m.id, m]));
    for (const m of incoming) map.set(m.id, m);
    const merged = Array.from(map.values()).sort((a, b) => a.id - b.id);
    return merged;
  };

  async function loadByConversation(id, opts = {}) {
    const { beforeId } = opts;
    const qs = new URLSearchParams();
    if (beforeId) qs.set('beforeId', String(beforeId));
    qs.set('limit', '30');

    const r = await apiJson(`/api/messages/dm/${id}?${qs.toString()}`);
    if (!r.ok) throw new Error(r.data?.error || r.text || 'Failed to load messages');
    if (!r.data?.ok) throw new Error(r.data?.error || 'Failed to load messages');

    setConvId(r.data.conversation?.id || id);
    setOtherUser(r.data.other_user ? { ...r.data.other_user, id: toInt(r.data.other_user.id) } : null);
    if (r.data.dm_state) setDmState(r.data.dm_state);

    const newMessages = r.data.messages || [];
    setMessages((prev) => dedupMerge(prev, newMessages));
    setNextBeforeId(r.data.next_before_id || null);

    // Mark delivered + read (best effort) — only for the newest page (avoid spam when loading older)
    const lastId = newMessages.length ? newMessages[newMessages.length - 1].id : 0;
    if (!beforeId && lastId) markDeliveredRead(id, lastId, { delivered: true, read: true });
  }

  async function loadWithUser(otherId) {
    const r = await apiJson(`/api/messages/dm/with/${otherId}`);
    if (!r.ok) throw new Error(r.data?.error || r.text || 'Failed to load conversation');
    if (!r.data?.ok) throw new Error(r.data?.error || 'Failed to load conversation');

    if (r.data.other_user) {
      setOtherUser({ ...r.data.other_user, id: toInt(r.data.other_user.id) });
    } else {
      setOtherUser({ id: otherId, name: `User #${otherId}` });
    }

    const existingId = toInt(r.data.conversation_id);
    if (existingId) {
      setConvId(existingId);
      await loadByConversation(existingId);
    } else {
      setConvId(0);
      setMessages([]);
      setNextBeforeId(null);
    }
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError('');
      try {
        if (conversationId) {
          await loadByConversation(toInt(conversationId));
        } else if (userId) {
          await loadWithUser(toInt(userId));
        } else {
          throw new Error('Missing conversation');
        }
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load chat');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, userId]);

  useEffect(() => {
    const handler = (payload) => {
      // Our debug logger prints Socket.IO args as Array(1). The real payload is args[0].
      // Also accept accidental array wrapping just in case.
      const p = Array.isArray(payload) ? (payload[0] || {}) : (payload || {});
      const pConvId = toInt(p.conversation_id || p.conversationId);
      if (!pConvId) return;

      // If this page was opened via /dm/u/:userId and convo wasn't created yet,
      // allow the first incoming message to bind the conversation.
      if (!convId && userId && toInt(userId) === toInt(p.other_user_id || p.otherUserId)) {
        setConvId(pConvId);
      }

      if (convId && pConvId !== convId) return;

      if (p.other_user) {
        setOtherUser({ ...p.other_user, id: toInt(p.other_user.id) });
      }

      if (p.message) {
        setMessages((prev) => dedupMerge(prev, [p.message]));
        setTimeout(scrollToBottom, 50);

        const msgId = toInt(p.message?.id);
        const senderId = toInt(p.message?.sender_id);
        // If we are viewing this DM and a new message arrives from the other user,
        // mark it delivered + read so the sender ticks update immediately.
        if (msgId && senderId && senderId !== meId) {
          markDeliveredRead(pConvId, msgId, { delivered: true, read: true });
        }
      }
    };


    const stateHandler = (payload) => {
      // Our debug logger prints Socket.IO args as Array(1). The real payload is args[0].
      // Also accept accidental array wrapping just in case.
      const p = Array.isArray(payload) ? (payload[0] || {}) : (payload || {});
      const pConvId = toInt(p.conversation_id || p.conversationId);
      if (!pConvId) return;
      if (convId && pConvId !== convId) return;

      // Only update ticks when the OTHER participant updates their state
      const fromUserId = toInt(p.updatedByUserId || p.fromUserId || p.user_id || p.userId);
      if (otherUser?.id && fromUserId && fromUserId !== toInt(otherUser.id)) return;

      setDmState((prev) => {
        const next = { ...prev };
        if (p.lastDeliveredMessageId || p.last_delivered_message_id || p.other_last_delivered_message_id || p.otherLastDeliveredMessageId) {
          next.other_last_delivered_message_id = Math.max(
            toInt(prev.other_last_delivered_message_id),
            toInt(p.lastDeliveredMessageId || p.last_delivered_message_id || p.other_last_delivered_message_id || p.otherLastDeliveredMessageId)
          );
        }
        if (p.lastReadMessageId || p.last_read_message_id || p.other_last_read_message_id || p.otherLastReadMessageId) {
          next.other_last_read_message_id = Math.max(
            toInt(prev.other_last_read_message_id),
            toInt(p.lastReadMessageId || p.last_read_message_id || p.other_last_read_message_id || p.otherLastReadMessageId)
          );
        }
        return next;
      });
    };

    socket.on('dm:newMessage', handler);
    socket.on('dm:stateUpdated', stateHandler);
    return () => {
      socket.off('dm:newMessage', handler);
      socket.off('dm:stateUpdated', stateHandler);
    };
  }, [convId, userId, otherUser?.id]);

  useEffect(() => {
    if (!loading) setTimeout(scrollToBottom, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function onLoadOlder() {
    if (!convId || !nextBeforeId) return;
    try {
      setLoadingMore(true);
      await loadByConversation(convId, { beforeId: nextBeforeId });
    } catch (e) {
      setError(e?.message || 'Failed to load older messages');
    } finally {
      setLoadingMore(false);
    }
  }

  async function onSend(e) {
    e.preventDefault();
    if (sending) return;

    const body = String(text || '').trim();
    if (!body) return;

    const toUserId = toInt(otherUser?.id);
    if (!toUserId) {
      setError('Missing recipient');
      return;
    }

    setSending(true);
    setError('');

    try {
      const r = await apiJson('/api/messages/dm/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_id: toUserId, body }),
      });
      if (!r.ok) throw new Error(r.data?.error || r.text || 'Send failed');
      if (!r.data?.ok) throw new Error(r.data?.error || 'Send failed');

      const newConvId = toInt(r.data.conversation_id);
      if (newConvId && !convId) setConvId(newConvId);

      if (r.data.other_user) {
        setOtherUser({ ...r.data.other_user, id: toInt(r.data.other_user.id) });
      }

      if (r.data.message) {
        setMessages((prev) => dedupMerge(prev, [r.data.message]));
      }

      setText('');
      setTimeout(scrollToBottom, 50);

      // Mark read (best effort) — guard against re-sends
      const lastId = toInt(r.data.message?.id);
      const cid = newConvId || convId;
      if (cid && lastId) markDeliveredRead(cid, lastId, { delivered: false, read: true });
    } catch (e2) {
      setError(e2?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="main-content chat-page">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <div className="chat-card">
        <div className="chat-header">
          <div>
            <Link to="/messages" className="chat-back">← Messages</Link>
            <h2 className="chat-title">{otherUser?.name || otherUser?.given_name || 'Direct chat'}</h2>
          </div>
        </div>

        {error && <p className="error-text" style={{ marginBottom: 8 }}>{error}</p>}

        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            {nextBeforeId && (
              <button
                className="chat-loadolder"
                onClick={onLoadOlder}
                disabled={loadingMore}
                type="button"
              >
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            )}

            <div className="chat-window">
              {messages.length === 0 ? (
                <div className="chat-empty">No messages yet. Say hi 👋</div>
              ) : (
                <>
                  {messages.map((m) => {
                    const mine = toInt(m.sender_id) === meId;
                    return (
                      <div key={m.id} className={`chat-row ${mine ? 'mine' : 'theirs'}`}>
                        <div className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                          <div className="chat-body">{m.body}</div>
                          <div className="chat-time">
                            {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                            {mine && (
                              <span
                                className={`chat-ticks ${
                                  toInt(dmState?.other_last_read_message_id) >= toInt(m.id) ? 'read' : ''
                                }`}
                                title={
                                  toInt(dmState?.other_last_read_message_id) >= toInt(m.id)
                                    ? 'Read'
                                    : toInt(dmState?.other_last_delivered_message_id) >= toInt(m.id)
                                    ? 'Delivered'
                                    : 'Sent'
                                }
                              >
                                {toInt(dmState?.other_last_read_message_id) >= toInt(m.id)
                                  ? '✓✓'
                                  : toInt(dmState?.other_last_delivered_message_id) >= toInt(m.id)
                                  ? '✓✓'
                                  : '✓'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </>
              )}
            </div>

            <form onSubmit={onSend} className="chat-composer">
              <textarea
                className="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                rows={2}
                maxLength={5000}
              />
              <button className="chat-send" type="submit" disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
